/**
 * New subscriptions page (V2) — radio-based plan selection per the redesign
 * (see src/app/assets/payment-temporary.png).
 *
 * Sections: free plan (current), premium plan options (intro monthly available,
 * the rest shown as "not available"), Yemenite-tradition kids plan, coupon input,
 * and a single Continue button that triggers the purchase for the selected plan.
 *
 * Purchase flow is identical to PaymentsPage: requestFlutterPurchase (WebView→Flutter)
 * → native IAP → server validation. Free coupons redeem via the redeemCoupon function.
 * The previous page remains available at /payments-classic until this one is final.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SubscriptionSuccessPopup from '../components/SubscriptionSuccessPopup';
import CurrentSubscriptionSection from '../components/CurrentSubscriptionSection';
import { requestFlutterPurchase } from '../components/useFlutterPurchase';
import { type CouponResult } from '../components/CouponInput';
import { FIREBASE_FUNCTIONS, callCloudFunction } from '../utils/firebaseFunctions';
import { trackAuth, trackPurchase } from '../utils/analytics';
import { useIsAdmin } from '../utils/useIsAdmin';
import { useHasActiveSubscription } from '../utils/useSubscription';
import { resolveStoreProductId } from '../utils/productIds';
import cheeringKidsImg from '../assets/webp/cheering-kids.webp';
import cloudsImg from '../assets/webp/clouds.webp';
import yamen1Img from '../assets/webp/yamen1.webp';
import BottomNav from '../components/BottomNav';

const LOGGED_IN_USER_KEY = 'loggedInUser';
const FREE_PLAN_ID = 'free';

function isRealLoggedInUser(userId: string | null): boolean {
  if (!userId || userId.trim() === '') return false;
  const lower = userId.toLowerCase();
  return lower !== 'guest' && !lower.startsWith('guest-');
}

interface StoredUser {
  userId: string;
  email: string | null;
  name: string;
  displayName: string;
  photoURL: string | null;
}

function createGuestUser(guestName: string): StoredUser {
  const guestUser: StoredUser = {
    userId: `guest-${crypto.randomUUID()}`,
    email: null,
    name: guestName,
    displayName: guestName,
    photoURL: null,
  };
  localStorage.setItem(LOGGED_IN_USER_KEY, JSON.stringify(guestUser));
  return guestUser;
}

interface PlanOption {
  id: string;
  titleKey: string;
  price: string;
  available: boolean;
  adminOnly?: boolean;
}

const PREMIUM_PLANS: PlanOption[] = [
  {
    id: 'month1',
    titleKey: 'payments_plan_monthly',
    price: '10$',
    available: true,
  },
  {
    id: '3months1',
    titleKey: 'payments_plan_3months',
    price: '30$',
    available: true,
  },
  {
    id: 'yearly',
    titleKey: 'payments_plan_yearly',
    price: '120$',
    available: true,
  },
];

const YEMEN_PLANS: PlanOption[] = [
  { id: 'yamen1', titleKey: 'payments_plan_monthly', price: '20$', available: false },
  { id: 'yamen1yearly1', titleKey: 'payments_plan_yearly', price: '120$', available: false },
];

// Regular prices that take effect once the intro offer ends — shown so users
// know today's prices are temporary, not selectable.
const FUTURE_PREMIUM_PLANS: PlanOption[] = [
  { id: 'future_month1', titleKey: 'payments_plan_monthly', price: '59$', available: false },
  { id: 'future_3months1', titleKey: 'payments_plan_3months', price: '149$', available: false },
  { id: 'future_yearly', titleKey: 'payments_plan_yearly', price: '229$', available: false },
];

const FREE_FEATURES: { key: string; included: boolean }[] = [
  { key: 'payments_feature_first_verses', included: true },
  { key: 'payments_feature_ai_limited', included: false },
  { key: 'payments_feature_analytics', included: false },
  { key: 'payments_feature_progress', included: false },
  { key: 'payments_feature_parent_tracking', included: false },
];

const PREMIUM_FEATURE_KEYS = [
  'payments_feature_full_access',
  'payments_feature_ai_instant',
  'payments_feature_analytics',
  'payments_feature_progress',
  'payments_feature_add_parents',
  'payments_feature_parent_learning',
];

const YEMEN_FEATURE_KEYS = [
  'payments_feature_yemen_aliyot',
  'payments_feature_ai_instant',
  'payments_feature_analytics',
  'payments_feature_progress',
  'payments_feature_add_parents',
];

// ─── Presentational helpers (used only by this page) ───────────────────────

interface FeatureItemProps {
  label: string;
  included?: boolean;
}

const FeatureItem: React.FC<FeatureItemProps> = ({ label, included = true }) => (
  <li className="flex items-center gap-1.5 whitespace-nowrap text-[15.6px] font-semibold leading-snug text-gray-900 max-[400px]:text-[12px]">
    <span
      aria-hidden
      className={`shrink-0 text-[20px] font-extrabold leading-none max-[400px]:text-[15.6px] ${
        included ? 'text-green-600' : 'text-red-600'
      }`}
    >
      {included ? '✓' : '✕'}
    </span>
    <span>{label}</span>
  </li>
);

interface RadioCircleProps {
  selected: boolean;
  disabled?: boolean;
  large?: boolean;
}

const RadioCircle: React.FC<RadioCircleProps> = ({ selected, disabled = false, large = false }) => (
  <span
    aria-hidden
    className={`flex shrink-0 items-center justify-center rounded-full border-4 border-[#0E2F72] max-[400px]:border-[3px] ${
      large ? 'h-9 w-9 max-[400px]:h-7 max-[400px]:w-7' : 'h-7 w-7 max-[400px]:h-6 max-[400px]:w-6'
    } ${selected ? 'bg-[#0E2F72] text-white' : 'bg-white'} ${disabled ? 'opacity-40' : ''}`}
  >
    {selected && (
      <span className={`font-extrabold leading-none ${large ? 'text-xl' : 'text-sm'}`}>✓</span>
    )}
  </span>
);

interface PriceBadgeProps {
  price: string;
  muted?: boolean;
  small?: boolean;
}

const PriceBadge: React.FC<PriceBadgeProps> = ({ price, muted = false, small = false }) => (
  <span
    className={`text-center font-extrabold text-white ${muted ? 'bg-[#6B7280]' : 'bg-[#3B7CF4]'} ${
      small
        ? 'min-w-[50px] rounded-lg px-2 py-0.5 text-sm max-[400px]:min-w-[42px] max-[400px]:text-[11px]'
        : 'min-w-[76px] rounded-2xl px-3.5 py-1 text-xl max-[400px]:min-w-[60px] max-[400px]:px-2.5 max-[400px]:text-base'
    }`}
  >
    {price}
  </span>
);

interface PlanOptionRowProps {
  plan: PlanOption;
  selected: boolean;
  onSelect: (id: string) => void;
}

const PlanOptionRow: React.FC<PlanOptionRowProps> = ({ plan, selected, onSelect }) => {
  const { t } = useTranslation();
  const handleSelect = (): void => {
    if (plan.available) onSelect(plan.id);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleSelect}
        disabled={!plan.available}
        aria-pressed={selected}
        className={`flex w-full items-center justify-between rounded-[20px] border-2 border-[#F2CC0D] px-3.5 py-3 max-[400px]:px-2.5 ${
          plan.available ? 'bg-white cursor-pointer' : 'bg-[#D9D9D9] cursor-not-allowed'
        }`}
      >
        <span
          className={`whitespace-nowrap text-2xl font-extrabold leading-none max-[400px]:text-[19px] ${
            plan.available ? 'text-gray-900' : 'text-gray-500'
          }`}
        >
          {t(plan.titleKey)}
        </span>
        <span className="flex items-center gap-2">
          {!plan.available && (
            <span className="me-1 whitespace-nowrap text-xs font-bold text-gray-900 max-[400px]:text-[10px]">
              {t('payments_not_available')}
            </span>
          )}
          <PriceBadge price={plan.price} muted={!plan.available} />
          <RadioCircle selected={selected} disabled={!plan.available} />
        </span>
      </button>
    </div>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────

const PaymentsPageV2: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const direction = (i18n.language?.startsWith('he') ?? false) ? 'rtl' : 'ltr';
  const isAdmin = useIsAdmin();
  const { hasActiveSubscription } = useHasActiveSubscription();
  const premiumPlans = PREMIUM_PLANS.map((plan) =>
    plan.adminOnly ? { ...plan, available: isAdmin } : plan,
  );

  const isLocalhost = window.location.hostname === 'localhost';
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const effectivelyLoggedIn = isLocalhost || isLoggedIn;
  // Any tracked identity — a real account or a guest id — is enough to purchase and trace later.
  const hasIdentity = isLocalhost || !!userId;
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>(FREE_PLAN_ID);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<CouponResult | null>(null);

  const readStoredUser = (): void => {
    const raw = localStorage.getItem(LOGGED_IN_USER_KEY);
    if (!raw) {
      setIsLoggedIn(false);
      setUserId(null);
      return;
    }
    try {
      const parsed = JSON.parse(raw) as { userId?: string; uid?: string; email?: string };
      const storedId = parsed?.userId ?? parsed?.uid ?? parsed?.email ?? null;
      setIsLoggedIn(isRealLoggedInUser(storedId));
      setUserId(storedId);
      setUserEmail(parsed?.email?.trim().toLowerCase() ?? null);
    } catch {
      setIsLoggedIn(false);
      setUserId(null);
    }
  };

  useEffect(() => {
    readStoredUser();
  }, []);

  const handleContinueAsGuest = (): void => {
    trackAuth('sign_in', 'guest', true);
    createGuestUser(t('guest', 'Guest'));
    readStoredUser();
  };

  const redeemFreeCoupon = async (productId: string): Promise<void> => {
    if (!appliedCoupon || appliedCoupon.discountType !== 'free' || !userEmail) return;
    try {
      const res = await callCloudFunction('redeemCoupon', FIREBASE_FUNCTIONS.redeemCoupon(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: appliedCoupon.code, email: userEmail, productId }),
      });
      const data = (await res.json()) as { success: boolean; expiresAt?: string; error?: string };
      if (data.success) {
        trackPurchase(productId, 'coupon', {
          coupon_code: appliedCoupon.code,
          coupon_type: appliedCoupon.discountType,
          user_id: userId,
          is_guest: !effectivelyLoggedIn,
        });
        setAppliedCoupon(null);
        setShowCongratulations(true);
      } else {
        setErrorText(data.error ?? t('coupon_redeem_error'));
      }
    } catch (err) {
      console.error('redeemFreeCoupon failed', err);
      setErrorText(t('coupon_redeem_error'));
    }
  };

  const purchase = async (productId: string): Promise<void> => {
    const isIosWebView =
      typeof window !== 'undefined' &&
      window.flutter_inappwebview !== undefined &&
      /iPhone|iPad|iPod/i.test(navigator.userAgent);
    let finalProductId = resolveStoreProductId(
      productId,
      isIosWebView,
      appliedCoupon?.discountType === 'discount' ? appliedCoupon.storePlanNamePostfix : undefined,
    );
    // TEST CODE — remove before shipping
    if (userEmail === 'shaul.almog@gmail.com' && productId === 'month1') {
      finalProductId = 'test';
    }
    try {
      const result = await requestFlutterPurchase(finalProductId, userId, finalProductId);
      if (result.success) {
        trackPurchase(productId, 'store', {
          store_product_id: finalProductId,
          platform: isIosWebView ? 'ios' : 'android_web',
          coupon_code: appliedCoupon?.code,
          coupon_type: appliedCoupon?.discountType,
          user_id: userId,
          is_guest: !effectivelyLoggedIn,
        });
        if (appliedCoupon?.discountType === 'percent') setAppliedCoupon(null);
        setShowCongratulations(true);
      } else {
        setErrorText(result.error ?? t('payments_error'));
      }
    } catch (err) {
      console.error('purchase failed', err);
      setErrorText(t('payments_error'));
    }
  };

  const handleContinue = async (): Promise<void> => {
    if (selectedId === FREE_PLAN_ID || isPurchasing) return;
    if (!hasIdentity) {
      setErrorText(t('payments_identity_required'));
      return;
    }
    if (hasActiveSubscription) {
      setErrorText(t('payments_already_subscribed'));
      return;
    }
    setErrorText(null);
    setIsPurchasing(true);
    try {
      if (appliedCoupon?.discountType === 'free') {
        await redeemFreeCoupon(selectedId);
      } else {
        await purchase(selectedId);
      }
    } finally {
      setIsPurchasing(false);
    }
  };

  const canContinue =
    selectedId !== FREE_PLAN_ID && hasIdentity && !isPurchasing && !hasActiveSubscription;

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-white">
      <div
        dir={direction}
        className="mx-auto w-full max-w-[480px] grow overflow-y-auto overflow-x-hidden pb-4"
      >
        {/* Hero: clouds background with cheering kids */}
        <div className="relative flex h-[210px] justify-center overflow-hidden">
          <img src={cloudsImg} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <img src={cheeringKidsImg} alt="" className="relative z-10 block h-full w-auto" />
        </div>

        {/* Blue ribbon */}
        <div className="bg-[#3B7CF4] px-3 py-1.5 text-center">
          <p className="m-0 whitespace-nowrap text-[15px] font-bold leading-normal text-white max-[400px]:text-xs">
            {t('payments_v2_ribbon')}
          </p>
        </div>

        <div className="px-4 py-5">
          {/* Identity choice: register or continue as guest */}
          {!hasIdentity && (
            <div
              role="alert"
              className="mb-5 rounded-xl border border-[#F2CC0D] bg-amber-100 p-4 text-[15px] leading-normal text-amber-800"
            >
              <p className="mb-3">{t('payments_register_or_guest_prompt')}</p>
              <div className="flex flex-wrap gap-2.5">
                <button
                  type="button"
                  onClick={() => navigate('/onboarding', { state: { returnTo: '/payments' } })}
                  className="rounded-lg bg-amber-600 px-5 py-2.5 text-[15px] font-semibold text-white"
                >
                  {t('payments_sign_in')}
                </button>
                <button
                  type="button"
                  onClick={handleContinueAsGuest}
                  className="rounded-lg border border-amber-600 bg-transparent px-5 py-2.5 text-[15px] font-semibold text-amber-800"
                >
                  {t('onboarding_guest')}
                </button>
              </div>
            </div>
          )}

          {/* Current subscription (if any) */}
          {isLoggedIn && <CurrentSubscriptionSection revalidateOnLoad />}

          {/* Free plan card */}
          <section className="mb-5 rounded-3xl border-2 border-gray-900 bg-[#DDE9F9] p-4">
            <div className="flex items-center justify-between gap-1">
              <div className="min-w-0">
                <h2 className="m-0 text-[40px] font-extrabold leading-none text-gray-900 max-[400px]:text-[32px]">
                  {t('payments_free_title')}
                </h2>
                {!hasActiveSubscription && (
                  <p className="m-0 mt-0.5 text-sm font-bold leading-snug text-gray-500 max-[400px]:text-[11px]">
                    {t('payments_free_current_plan')}
                  </p>
                )}
                <ul className="m-0 mt-1.5 flex list-none flex-col gap-0 p-0">
                  {FREE_FEATURES.map((f) => (
                    <FeatureItem key={f.key} label={t(f.key)} included={f.included} />
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={() => setSelectedId(FREE_PLAN_ID)}
                aria-pressed={selectedId === FREE_PLAN_ID}
                className="p-0 flex shrink-0 items-center gap-2 self-center bg-transparent"
              >
                <PriceBadge price="0$" />
                <RadioCircle selected={selectedId === FREE_PLAN_ID} large />
              </button>
            </div>
          </section>

          {/* Premium plan card */}
          <section className="mb-5 rounded-3xl border-2 border-gray-900 bg-[#F9FAFC] px-4 pb-4 pt-3">
            <div className="px-3">
              <h2 className="m-0 flex items-center gap-2.5 text-[40px] font-extrabold leading-none text-gray-900 max-[400px]:text-[32px]">
                {t('payments_premium_title')}
                <span
                  aria-hidden
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F2CC0D] text-sm leading-none text-white"
                >
                  ★
                </span>
              </h2>
              <ul className="m-0 mt-2 flex list-none flex-col gap-0 p-0">
                {PREMIUM_FEATURE_KEYS.map((key) => (
                  <FeatureItem key={key} label={t(key)} />
                ))}
              </ul>
            </div>
            <div className="relative mt-6 rounded-[24px] border-2 border-[#F2CC0D] px-4 pt-5">
              <span className="absolute -top-3.5 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg bg-[#F2CC0D] px-4 py-0.5 text-sm font-bold text-red-600 max-[400px]:text-[12px]">
                {t('payments_intro_price_tag')}
              </span>
              <div className="flex flex-col gap-4">
                {premiumPlans.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedId(plan.id)}
                    aria-pressed={selectedId === plan.id}
                    className="flex w-full items-center justify-between bg-transparent p-0"
                  >
                    <span className="whitespace-nowrap text-2xl font-extrabold leading-none text-gray-900 max-[400px]:text-[19px]">
                      {t(plan.titleKey)}
                    </span>
                    <span className="flex items-center gap-2">
                      <PriceBadge price={plan.price} />
                      <RadioCircle selected={selectedId === plan.id} />
                    </span>
                  </button>
                ))}
              </div>
              <p className="m-0 text-center text-sm font-extrabold text-red-600 max-[400px]:text-xs">
                {t('payments_limited_time_note')}
              </p>
            </div>

            {/* Regular prices that take effect once the intro offer ends */}
            <p className="m-0 mb-3 mt-4 text-center text-xs font-bold leading-snug text-gray-500 max-[400px]:text-[12px]">
              {t('payments_future_price_note')}
            </p>
            <div className="flex flex-col gap-4">
              {FUTURE_PREMIUM_PLANS.map((plan) => (
                <PlanOptionRow key={plan.id} plan={plan} selected={false} onSelect={() => {}} />
              ))}
            </div>
          </section>

          {/* Yemenite kids plan card */}
          <section className="mb-5 overflow-hidden rounded-3xl border-2 border-gray-900 bg-white">
            <div className="px-4 pt-3">
              <h2 className="m-0 text-[34px] font-extrabold leading-tight text-gray-900 max-[400px]:text-[27px]">
                {t('payments_yemen_title')}
              </h2>
              <p className="m-0 mt-0.5 text-sm font-bold leading-snug text-gray-500 max-[400px]:text-[11px]">
                {t('payments_yemen_subtitle')}
              </p>
            </div>
            <div className="flex items-end gap-1 ps-4">
              <ul className="m-0 mt-1.5 flex min-w-0 flex-1 list-none flex-col gap-0 p-0 pb-2.5">
                {YEMEN_FEATURE_KEYS.map((key) => (
                  <FeatureItem key={key} label={t(key)} />
                ))}
              </ul>
              <img src={yamen1Img} alt="" className="block h-36 w-auto shrink-0" />
            </div>
            <div className="flex divide-x-2 divide-x-reverse divide-gray-900 border-t-2 border-gray-900 bg-[#D1D5DB]">
              {YEMEN_PLANS.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => plan.available && setSelectedId(plan.id)}
                  disabled={!plan.available}
                  aria-pressed={selectedId === plan.id}
                  className={`flex flex-1 items-center justify-between gap-1 rounded-none px-2.5 py-2 ${
                    plan.available ? 'cursor-pointer' : 'cursor-not-allowed'
                  }`}
                >
                  <span className="whitespace-nowrap text-base font-extrabold leading-none text-gray-500 max-[400px]:text-[13px]">
                    {t(plan.titleKey)}
                  </span>
                  <span className="flex items-center gap-1">
                    <PriceBadge price={plan.price} muted small />
                    <RadioCircle selected={selectedId === plan.id} disabled={!plan.available} />
                  </span>
                </button>
              ))}
            </div>
          </section>

          {/* Coupon */}
          {/*<CouponInput*/}
          {/*  compact*/}
          {/*  onValidCoupon={(result) => setAppliedCoupon(result)}*/}
          {/*  onClearCoupon={() => setAppliedCoupon(null)}*/}
          {/*/>*/}
        </div>

        <SubscriptionSuccessPopup
          open={showCongratulations}
          onClose={() => setShowCongratulations(false)}
        />
      </div>

      {/* Pinned footer: error, Continue button and note stay fixed above the bottom nav */}
      <div
        dir={direction}
        className="mx-auto w-full max-w-[480px] shrink-0 border-t border-gray-200 bg-white px-4 pb-2 pt-3"
      >
        {/* Already-subscribed notice — shown proactively so the disabled Continue
            button isn't a mystery, on top of the same guard in handleContinue. */}
        {hasActiveSubscription && selectedId !== FREE_PLAN_ID && (
          <div role="alert" className="mb-3 rounded-lg bg-amber-100 p-3 text-sm text-amber-800">
            {t('payments_already_subscribed')}
          </div>
        )}

        {/* Error message */}
        {errorText && (
          <div role="alert" className="mb-3 rounded-lg bg-red-100 p-3 text-sm text-red-800">
            {errorText}
          </div>
        )}

        {/* Continue */}
        <button
          type="button"
          onClick={() => void handleContinue()}
          disabled={!canContinue}
          className={`w-full rounded-[18px] py-2.5 text-[22px] font-bold text-white max-[400px]:text-sm ${
            canContinue ? 'bg-[#3B7CF4] cursor-pointer' : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          {isPurchasing ? '…' : t('payments_continue')}
        </button>

        {/* Footer note */}
        <p className="mt-2.5 whitespace-nowrap text-center text-sm font-bold text-gray-900 max-[400px]:text-xs">
          {t('payments_footer_note')}
        </p>
      </div>
      <BottomNav />
    </div>
  );
};

export default PaymentsPageV2;
